// Prospecting funnel (spec 09 §3.1). SQL mirror: the `prospecting` part of public.funnel_report_at.
import {
  DEFAULT_DEAL_STAGES,
  isAfterQualifie,
  type StageDef,
} from "../deals/stageModel";
import type { FunnelReport, ReportFilters } from "../../reports/types";
import {
  companyMatches,
  type FunnelCall,
  type FunnelCompany,
  type FunnelDeal,
  type StageEvent,
} from "./dealFunnel";
import { periodBounds } from "./period";

export const CONTACT_OUTCOMES: ReadonlySet<string> = new Set([
  "talked_callback",
  "demo_booked",
  "brochure_sent",
  "not_interested",
]);
export const INTERESTED_OUTCOMES: ReadonlySet<string> = new Set([
  "talked_callback",
  "demo_booked",
  "brochure_sent",
]);

export const computeProspectingFunnel = (input: {
  calls: readonly FunnelCall[];
  companies: readonly FunnelCompany[];
  deals: readonly FunnelDeal[];
  events: readonly StageEvent[];
  from: string;
  to: string;
  filters: ReportFilters;
  stages?: readonly StageDef[];
}): FunnelReport["prospecting"] => {
  const stages = input.stages ?? DEFAULT_DEAL_STAGES;
  const { start, end } = periodBounds(input.from, input.to);
  const byId = new Map(input.companies.map((c) => [c.id, c]));
  const calls = input.calls.filter((c) => {
    const t = Date.parse(c.dialed_at);
    return (
      c.direction === "outbound" &&
      c.company_id != null &&
      c.outcome !== "skipped" &&
      t >= start &&
      t < end &&
      (input.filters.sales_id == null ||
        c.sales_id === input.filters.sales_id) &&
      companyMatches(byId.get(c.company_id), input.filters)
    );
  });
  const companiesWhere = (pred: (c: FunnelCall) => boolean) =>
    new Set(calls.filter(pred).map((c) => c.company_id)).size;
  const cohort = new Set(calls.map((c) => c.company_id));
  const pastQualified = new Set(
    input.events
      .filter((e) => isAfterQualifie(stages, e.to_stage))
      .map((e) => e.deal_id),
  );
  const held = new Set(
    input.deals
      .filter(
        (d) =>
          d.company_id != null &&
          cohort.has(d.company_id) &&
          (Boolean(d.demo_held_at) ||
            isAfterQualifie(stages, d.stage) || // SQL: public.gavion_stage_after_qualifie
            pastQualified.has(d.id)),
      )
      .map((d) => d.company_id),
  );
  return [
    { key: "called", count: cohort.size },
    {
      key: "reached",
      count: companiesWhere((c) => CONTACT_OUTCOMES.has(c.outcome ?? "")),
    },
    {
      key: "interested",
      count: companiesWhere((c) => INTERESTED_OUTCOMES.has(c.outcome ?? "")),
    },
    {
      key: "demos_booked",
      count: companiesWhere((c) => c.outcome === "demo_booked"),
    },
    { key: "demos_held", count: held.size },
  ];
};
